/**
 * Maps template/picker `fontFamily` ids to how they are rendered on canvas.
 *
 * - google: load via Google Fonts CDN (correct weights; avoids wrong/mislabeled .ttf files)
 * - local: self-hosted @font-face in editor.scss (brand / custom faces only)
 *
 * Audit notes (fc-query on public/fonts/*):
 * - Antonio.ttf → internal family "Anton" (do not use file)
 * - Poppins.ttf → SemiBold only (not regular)
 * - Claredon.ttf → Clarendon Black
 * - Domaine.otf → Semibold Italic only
 * - Fontspring.otf → Geometos Neue DEMO
 * - Gothic.ttf → Century Gothic (intentional alias "Gothic")
 */

export type FontSource = 'google' | 'local';

export type FontFamilyRegistryEntry = {
  source: FontSource;
  /** CSS family name for document.fonts / Konva when different from stored id */
  googleFamily?: string;
};

/**
 * Stored fontFamily (designState + picker `value`) → render config.
 * Only list families we ship; unknown ids fall through as local.
 */
export const FONT_FAMILY_REGISTRY: Record<string, FontFamilyRegistryEntry> = {
  // Google — overlap names (picker id kept; render via CDN)
  Poppins: { source: 'google' },
  Anton: { source: 'google' },
  Allura: { source: 'google' },
  Italianno: { source: 'google' },
  GreatVibes: { source: 'google', googleFamily: 'Great Vibes' },
  Antonio: { source: 'google' },

  // Google — was local .ttf but file wrong weight or on Google catalog
  ABeeZee: { source: 'google' },
  Philosopher: { source: 'google' },
  AlexBrush: { source: 'google', googleFamily: 'Alex Brush' },
  Amaranth: { source: 'google' },
  /** Local TTF matches; Google WOFF2 looked different in templates — keep file. */
  BerkshireSwash: { source: 'local' },
  LibreBaskerville: { source: 'google', googleFamily: 'Libre Baskerville' },
  PetitFormalScript: { source: 'google', googleFamily: 'Petit Formal Script' },
  FiraSansCondensed: { source: 'google', googleFamily: 'Fira Sans Condensed' },

  // Google — catalog-only (never had a correct local file)
  Inter: { source: 'google' },
  Montserrat: { source: 'google' },
  Roboto: { source: 'google' },
  'Open Sans': { source: 'google' },
  Raleway: { source: 'google' },
  Nunito: { source: 'google' },
  Rubik: { source: 'google' },
  'Work Sans': { source: 'google' },
  Figtree: { source: 'google' },
  Lexend: { source: 'google' },
  'Josefin Sans': { source: 'google' },
  'Space Grotesk': { source: 'google' },
  'Titillium Web': { source: 'google' },
  'DM Sans': { source: 'google' },
  Manrope: { source: 'google' },
  Outfit: { source: 'google' },
  'Plus Jakarta Sans': { source: 'google' },
  Urbanist: { source: 'google' },
  'Playfair Display': { source: 'google' },
  'Cormorant Garamond': { source: 'google' },
  Cinzel: { source: 'google' },
  Merriweather: { source: 'google' },
  Fraunces: { source: 'google' },
  'Bodoni Moda': { source: 'google' },
  Lora: { source: 'google' },
  'DM Serif Display': { source: 'google' },
  'Dancing Script': { source: 'google' },
  Parisienne: { source: 'google' },
  'Pinyon Script': { source: 'google' },
  Tangerine: { source: 'google' },
  Satisfy: { source: 'google' },
  Carattere: { source: 'google' },
  'Meie Script': { source: 'google' },
  Pacifico: { source: 'google' },
  Caveat: { source: 'google' },
  Yellowtail: { source: 'google' },
  'Bebas Neue': { source: 'google' },
  Staatliches: { source: 'google' },
  Teko: { source: 'google' },
  Righteous: { source: 'google' },
  'Paytone One': { source: 'google' },
  'Russo One': { source: 'google' },
  Bungee: { source: 'google' },
  Fredoka: { source: 'google' },
  Shrikhand: { source: 'google' },
  'League Spartan': { source: 'google' },
  'Abril Fatface': { source: 'google' },
  Oswald: { source: 'google' },
  'Lilita One': { source: 'google' },
  'Archivo Black': { source: 'google' },
  'Roboto Condensed': { source: 'google' },
  'Barlow Condensed': { source: 'google' },
  'Archivo Narrow': { source: 'google' },
  'Saira Condensed': { source: 'google' },

  // Local — custom / brand (keep @font-face)
  Abuget: { source: 'local' },
  AlexisMarie: { source: 'local' },
  ArgoFlats: { source: 'local' },
  bakery: { source: 'local' },
  BetterTimes: { source: 'local' },
  Blackjack: { source: 'local' },
  Claredon: { source: 'local' },
  Domaine: { source: 'local' },
  Enchanting: { source: 'local' },
  Fontspring: { source: 'local' },
  Gothic: { source: 'local' },
  Lhandw: { source: 'local' },
  Manta: { source: 'local' },
  Mvboli: { source: 'local' },
  Thunder: { source: 'local' },
  Wayfarer: { source: 'local' },
  Georgia: { source: 'local' },
  TimesNewRoman: { source: 'local' },
};

export const resolveFontFamilyForCanvas = (storedFamily: string): string => {
  const entry = FONT_FAMILY_REGISTRY[storedFamily];
  if (entry?.source === 'google') {
    return entry.googleFamily ?? storedFamily;
  }
  return storedFamily;
};

export const isGoogleTextFontFamily = (storedFamily: string): boolean =>
  FONT_FAMILY_REGISTRY[storedFamily]?.source === 'google';

/** Unique Google CSS family names for the stylesheet URL + preload. */
export const GOOGLE_TEXT_FONT_FAMILIES: string[] = Array.from(
  new Set(
    Object.entries(FONT_FAMILY_REGISTRY)
      .filter(([, entry]) => entry.source === 'google')
      .map(([id, entry]) => entry.googleFamily ?? id),
  ),
).sort();
